import React, { useCallback, useContext, useMemo } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Row from '../../../../components/Common/Row';
import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import CurrentUserContext from '../../../../contexts/CurrentUserContext';
import EntityDefinitionsContext from '../../../../contexts/EntityDefinitionsContext';
import ToastContext from '../../../../contexts/ToastContext';
import { PortalsContext } from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import getIdentifier from '../../../../getIdentifier';
import getTimeStamp from '../../../../getTimeStamp';
import usePopup from '../../../../hooks/usePopup';
import ContextMenu, { Option } from '../../../../kingpin/atoms/ContextMenu';
import Typography from '../../../../kingpin/atoms/Typography';
import { buildShowEntityApp } from '../../../../navigation/appRoutes';
import STORE from '../../../../store';

import DeleteEntityAppPopup from './DeleteEntityAppPopup';
import EntityAppForm from './EntityAppForm';
import EntityAppUsage from './EntityAppUsage';

const ItemDivInner = styled.div`
  display: flex;
  height: 40px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  user-select: none;
  flex: 1;
`;

const ItemDivOuter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 8px 0px 0px;
  cursor: pointer;

  &:not(:last-of-type) {
    border-bottom: 1px solid var(--color-border-secondary);
  }

  &:hover {
    opacity: 0.7;
    background-color: var(--color-bg-hover);
  }
`;

const EntityAppListItem = ({
  entity,
  entityApp,
}: {
  entity: EntityDetails.Entity;
  entityApp: EntityDetails.App;
}) => {
  const { selectedPortal } = useContext(PortalsContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { entityDetailsApps } = useContext(EntityDefinitionsContext);
  const { showToast } = useContext(ToastContext);
  const { isOpen: isEditOpen, open: openEdit, close: closeEdit } = usePopup();
  const {
    isOpen: isUsageReportOpen,
    open: openUsageReport,
    close: closeUsageReport,
  } = usePopup();
  const {
    isOpen: isDeleteOpen,
    open: openDelete,
    close: closeDelete,
  } = usePopup();

  const linkTo = useMemo(() => {
    return buildShowEntityApp({
      entityApp,
      portal: selectedPortal,
    });
  }, [entityApp, selectedPortal]);

  const onDuplicate = useCallback(async () => {
    const existingSlugs = new Set(entityDetailsApps.map((a) => a.urlSlug));
    const baseSlug = `${entityApp.urlSlug}-copy`;
    let slug = baseSlug;
    let counter = 2;
    while (existingSlugs.has(slug)) {
      slug = `${baseSlug}-${counter}`;
      counter++;
    }

    const newApp: EntityDetails.App = {
      ...entityApp,
      id: getIdentifier(),
      title: `Copy of ${entityApp.title}`,
      urlSlug: slug,
      createdBy: currentUserId,
      createdOn: getTimeStamp(),
      updatedBy: currentUserId,
      updatedOn: getTimeStamp(),
    };

    await STORE.contentDistributions
      .getEntityDetailsApps({ accountId: selectedAccountId })
      .doc(newApp.id)
      .set(newApp);

    showToast('App duplicated');
  }, [
    currentUserId,
    entityApp,
    entityDetailsApps,
    selectedAccountId,
    showToast,
  ]);

  return (
    <>
      <ItemDivOuter>
        <ItemDivInner style={{ justifyContent: 'space-between' }}>
          <Row centerAlign>
            <div style={{ width: 200 }}>
              <Link to={linkTo}>
                <Typography.Body type={'Body 13'} isLink>
                  {entityApp.title}
                </Typography.Body>
              </Link>
            </div>
            <div style={{ width: 200 }}>
              <Typography.Body type={'Body 13'} color={'var(--color-grey-5)'}>
                {`/${entityApp.urlSlug}`}
              </Typography.Body>
            </div>
          </Row>
          <ContextMenu testId={`app-options-${entityApp.id}`}>
            <Option label={'Edit'} onClick={openEdit} />
            <Option label={'Duplicate'} onClick={onDuplicate} />
            <Option label={'Usage Report'} onClick={openUsageReport} />
            <Option label={'Delete'} onClick={openDelete} />
          </ContextMenu>
        </ItemDivInner>
      </ItemDivOuter>
      <ModalTransition>
        {isEditOpen && (
          <EntityAppForm
            entity={entity}
            close={closeEdit}
            entityApp={entityApp}
          />
        )}
      </ModalTransition>
      <ModalTransition>
        {isUsageReportOpen && (
          <EntityAppUsage entityApp={entityApp} close={closeUsageReport} />
        )}
      </ModalTransition>
      <ModalTransition>
        {isDeleteOpen && (
          <DeleteEntityAppPopup entityApp={entityApp} close={closeDelete} />
        )}
      </ModalTransition>
    </>
  );
};

export default EntityAppListItem;
